/* ============================================================
 * SMRITI-OS — Shoper9-Based Retail OS
 * Zero Cloud · Sovereign · AI-Governed
 * ============================================================
 * System Architect   :  Jawahar R Mallah
 * Organisation       :  AITDL Network
 * Project : SMRITI-OS
 * © 2026 — All Rights Reserved
 * "Memory, Not Code."
 * ============================================================ */
import axios from 'axios'
import { supabase } from '@/lib/supabase'
import { useSovereignStore } from '@/core/stores/useSovereignStore'
import { useRuntimeConfigStore } from '@/core/stores/useRuntimeConfigStore'

const CLOUD_API_FALLBACK = 'https://smriti-api.primesetu.com'

const getBaseUrl = () => {
  const { preferredBackendUrl } = useSovereignStore.getState();
  const { config } = useRuntimeConfigStore.getState();
  
  // 1. [PRIORITY] User's Explicit Override (Manual Switch / Troubleshooting)
  if (preferredBackendUrl) {
    return preferredBackendUrl;
  }

  // 2. [SOURCE OF TRUTH] Config written by Setup Wizard / Bootstrap
  if (config?.BACKEND_URL) {
    return config.BACKEND_URL;
  }

  // 3. [ENV FALLBACK] Cloud fallback for non-local environments
  if (!window.location.hostname.includes('localhost') && !window.location.hostname.includes('127.0.0.1')) {
    return CLOUD_API_FALLBACK;
  }

  // 4. [DEVELOPMENT FALLBACK] Local discovery
  return `http://${window.location.hostname}:8000`;
}

// We don't hardcode BASE_URL here anymore because the toggle can change at runtime.
// We will intercept every request and dynamically set the baseURL.

export const apiClient = axios.create({
  // Initial fallback, will be overwritten by interceptor
  baseURL: `${getBaseUrl().replace(/\/$/, '')}/api/v1`,
  withCredentials: true, // [GOVERNANCE] Mandatory for hardened CORS with specific origins
  headers: {
    'Content-Type': 'application/json',
  },
})

// Inject local JWT into every request + dynamically route based on Offline State
apiClient.interceptors.request.use((config) => {
  // Dynamically set the base URL before every request
  config.baseURL = `${getBaseUrl().replace(/\/$/, '')}/api/v1`

  // [R1-D] Inject stored local JWT as Bearer token.
  // In LOCAL_POSTGRES mode this token is issued by POST /api/v1/auth/local-login.
  // In CLOUD mode this should be the Supabase session token (set externally).
  const token = localStorage.getItem('smriti_local_token')
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`
  }

  const dbName = localStorage.getItem('X-Company-Db')
  if (dbName) {
    config.headers['X-Company-Db'] = dbName
  }

  return config
})

// Response interceptor: Handles retries, session expiration, and errors.
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { config, response } = error;

    // [R1-D] 401 Unauthorized → Session expired.
    if (response?.status === 401) {
      localStorage.removeItem('smriti_local_token');
      console.warn('[SMRITI-OS] Session expired (401). Token cleared.');
      return Promise.reject(error);
    }

    // [RETRY LOGIC] Calibrated for unstable retail WiFi/Tethering.
    // Rule: Retries are ONLY for idempotent requests (GET or carrying an idempotency key).
    const isIdempotent = config?.method?.toUpperCase() === 'GET' || !!config?.headers?.['x-idempotency-key'];
    const isNetworkError = !response || error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK';
    
    if (isIdempotent && isNetworkError && !config._retry) {
      config._retry = true;
      console.warn(`[SMRITI-OS] Retrying idempotent request: ${config.url}`);
      return apiClient(config); // Recursive retry (max 1)
    }

    return Promise.reject(error);
  }
);

export const api = {
  auth: {
    localStatus: () => apiClient.get('/auth/local-status').then(r => r.data),
    localLogin: (username: string, pin: string, store_id?: string) =>
      apiClient.post('/auth/local-login', { username, pin, store_id }).then(r => r.data),
  },
  dashboard: {
    getStats: () => apiClient.get('/dashboard/stats').then(r => r.data),
  },
  inventory: {
    list: () => apiClient.get('/inventory').then(r => r.data),
    create: (data: any) => apiClient.post('/products', data).then(r => r.data),
    update: (id: string, data: any) => apiClient.patch(`/products/${id}`, data).then(r => r.data),
    delete: (id: string) => apiClient.delete(`/products/${id}`).then(r => r.data),
    bulkCreate: (data: any[]) => apiClient.post('/products/bulk', data).then(r => r.data),
    search: (q: string) => apiClient.get(`/inventory/search?q=${q}`).then(r => r.data),
    advancedSearch: (data: { filters: any[], logic: string }) => apiClient.post('/inventory/advanced-search', data).then(r => r.data),
    getAlerts: () => apiClient.get('/inventory/alerts').then(r => r.data),
    getPredictive: () => apiClient.get('/inventory/predictive').then(r => r.data),
    getForecast: (tier?: string) => apiClient.get('/inventory/forecast', { params: { tier } }).then(r => r.data),
    getNetworkStock: (sku: string) => apiClient.get(`/inventory/${sku}/network-stock`).then(r => r.data),
    createISTRequest: (data: { sku: string, from_store_id: string, qty: number }) => apiClient.post('/inventory/ist/request', data).then(r => r.data),
    listPendingIST: () => apiClient.get('/inventory/ist/pending').then(r => r.data),
    getStockPrediction: (id: string) => apiClient.get(`/inventory/predictive/${id}`).then(r => r.data),
    createAuditSession: () => apiClient.post('/inventory/audit/sessions').then(r => r.data),
    listAuditSessions: () => apiClient.get('/inventory/audit/sessions').then(r => r.data),
    getAuditSession: (id: string) => apiClient.get(`/inventory/audit/sessions/${id}`).then(r => r.data),
    addAuditEntry: (auditId: string, data: any) => apiClient.post(`/inventory/audit/sessions/${auditId}/entries`, data).then(r => r.data),
    submitAudit: (id: string) => apiClient.post(`/inventory/audit/sessions/${id}/finalize`).then(r => r.data),
    generateInternal: (itemId: string) => apiClient.post('/barcodes/generate-internal', { item_id: itemId, is_primary: true, barcode_type: 'CODE128' }).then(r => r.data),
    generateEAN13: (itemId: string) => apiClient.post('/barcodes/generate-ean13', { item_id: itemId, is_primary: true, barcode_type: 'EAN13' }).then(r => r.data),
    printBarcode: (data: any) => apiClient.post('/barcodes/print', data).then(r => r.data),
    printBarcodeBatch: (data: any) => apiClient.post('/barcodes/print-batch', data).then(r => r.data),
    bulkUpdate: (items: any[]) => apiClient.post('/inventory/bulk-update', { items }).then(r => r.data),
  },
  procurement: {
    getSuggestions: () => apiClient.get('/procurement/reorder-suggestions').then(r => r.data),
    createPO: (data: any) => apiClient.post('/purchase/', data).then(r => r.data),
    processGRN: (data: any) => apiClient.post('/purchase/grn', data).then(r => r.data),
    listPOs: () => apiClient.get('/purchase/').then(r => r.data),
  },
  catalogue: {
    getPartners: (type?: string, q?: string) => apiClient.get('/catalogue/partners', { params: { type, q } }).then(r => r.data),
    getLookups: (category?: string) => apiClient.get('/catalogue/lookups', { params: { category } }).then(r => r.data),
    universalSearch: (q: string) => apiClient.get('/catalogue/universal-search', { params: { q } }).then(r => r.data),
    getPartnerMatrix: (id: string) => apiClient.get(`/catalogue/partners/${id}/matrix`).then(r => r.data),
    bulkPriceRevision: (revisions: any[]) => apiClient.post('/catalogue/price-revisions/bulk', revisions).then(r => r.data),
  },
  customers: {
    list: () => apiClient.get('/catalogue/partners?type=CUSTOMER').then(r => r.data),
  },
  vendors: {
    list: () => apiClient.get('/catalogue/partners?type=VENDOR').then(r => r.data),
  },
  billing: {
    finalize: (data: any) => apiClient.post('/billing/finalize', data).then(r => r.data),
    getHistory: () => apiClient.get('/billing/history').then(r => r.data),
    getDayEndSummary: () => apiClient.get('/billing/day-end/summary').then(r => r.data),
    finalizeDayEnd: () => apiClient.post('/billing/day-end/finalize').then(r => r.data),
    suspend: (data: any) => apiClient.post('/billing/suspend', data).then(r => r.data),
    getSuspended: () => apiClient.get('/billing/suspended').then(r => r.data),
    recallSuspended: (id: string) => apiClient.post(`/billing/suspended/${id}/recall`).then(r => r.data),
    deleteSuspended: (id: string) => apiClient.delete(`/billing/suspended/${id}`).then(r => r.data),
    getDayEnd: () => apiClient.get('/billing/day-end-summary').then(r => r.data),
    getBill: (billNo: string) => apiClient.get(`/billing/search/${billNo}`).then(r => r.data),
    getSlips: () => apiClient.get('/billing/slips').then(r => r.data),
    createSlip: (data: any) => apiClient.post('/billing/slips', data).then(r => r.data),
    deleteSlip: (id: string) => apiClient.delete(`/billing/slips/${id}`).then(r => r.data),
    convertSlip: (id: string) => apiClient.post(`/billing/slips/${id}/convert`).then(r => r.data),
    getPersonnel: () => apiClient.get('/billing/personnel').then(r => r.data),
    getPayModes: () => apiClient.get('/billing/paymodes').then(r => r.data),
    searchCustomers: (q: string) => apiClient.get('/customers/search', { params: { q } }).then(r => r.data),
    calculatePromos: (data: any) => apiClient.post('/billing/calculate-promos', data).then(r => r.data),
    getDraft: () => apiClient.get('/billing/draft').then(r => r.data),
    addToDraft: (data: any) => apiClient.post('/billing/draft', data).then(r => r.data),
    removeFromDraft: (id: string) => apiClient.delete(`/billing/draft/${id}`).then(r => r.data),
    clearDraft: () => apiClient.delete('/billing/draft/clear').then(r => r.data),
  },
  accounts: {
    issueCreditNote: (data: any) => apiClient.post('/accounts/credit-notes', data).then(r => r.data),
    getCustomerCreditNotes: (customerId: string) => apiClient.get(`/accounts/credit-notes/${customerId}`).then(r => r.data),
    receiveAdvance: (data: any) => apiClient.post('/accounts/advances', data).then(r => r.data),
    getGSTR1Summary: () => apiClient.get('/accounts/gstr1/summary').then(r => r.data),
    generateTallyXML: () => apiClient.get('/accounts/tally-export/xml').then(r => r.data),
  },
  integration: {
    exportTally: (start_date?: string, end_date?: string) => apiClient.get('/tally/export', { params: { start_date, end_date } }).then(r => r.data),
    importPDT: (file: File) => {
      const formData = new FormData()
      formData.append('file', file)
      return apiClient.post('/integration/pdt-import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      }).then(r => r.data)
    }
  },
  schemes: {
    list: () => apiClient.get('/schemes').then(r => r.data),
    create: (data: any) => apiClient.post('/schemes', data).then(r => r.data),
  },

  ho: {
    getStatus: () => apiClient.get('/ho/status').then(r => r.data),
    pulse: (data: any) => apiClient.post('/ho/pulse', data).then(r => r.data),
    confirmCommand: (id: string, managerId: string, pin: string) => apiClient.post(`/ho/confirm-command/${id}`, { manager_id: managerId, manager_pin: pin }).then(r => r.data),
    executeCommand: (id: string) => apiClient.post(`/ho/execute-command/${id}`).then(r => r.data),
    triggerSync: () => apiClient.post('/ho/sync').then(r => r.data),
  },
  reports: {
    getSalesByAttribute: (attr: string) => apiClient.get(`/reports/sales-by-attribute?attribute=${attr}`).then(r => r.data),
    getSalesSummary: () => apiClient.get('/reports/sales-summary').then(r => r.data),
    getInventoryValuation: () => apiClient.get('/reports/inventory-valuation').then(r => r.data),
    generateFlexibleReport: (config: any) => apiClient.post('/reports/flexible/', config).then(r => r.data),
  },
  reporting_engine: {
    listTemplates: () => apiClient.get('/reporting/templates').then(r => r.data),
    createTemplate: (data: any) => apiClient.post('/reporting/templates', data).then(r => r.data),
    listConfigs: () => apiClient.get('/reporting/configs').then(r => r.data),
    createConfig: (data: any) => apiClient.post('/reporting/configs', data).then(r => r.data),
  },
  compliance: {
    getGstr1: (month: number, year: number) => apiClient.get('/gstr1/export', { params: { month, year, fmt: 'json' } }).then(r => r.data),
    downloadGstr1Csv: (month: number, year: number) => apiClient.get('/gstr1/export', { params: { month, year, fmt: 'csv' }, responseType: 'blob' }).then(r => r.data),
  },
  tills: {
    list: () => apiClient.get('/finance/till').then(r => r.data),
    create: (data: any) => apiClient.post('/finance/till', data).then(r => r.data),
    open: (id: string, data: any) => apiClient.post(`/finance/till/${id}/open`, data).then(r => r.data),
    close: (id: string) => apiClient.post(`/finance/till/${id}/close`).then(r => r.data),
    lift: (id: string, data: any) => apiClient.post(`/finance/till/${id}/lift`, data).then(r => r.data),
  },
  security: {
    listGroups: () => apiClient.get('/security/groups').then(r => r.data),
    createGroup: (data: any) => apiClient.post('/security/groups', data).then(r => r.data),
    assignUser: (data: {user_id: string, group_id: string}) => apiClient.post('/security/assign', data).then(r => r.data),
  },
  store: {
    getSettings: () => apiClient.get('/store/settings').then(r => r.data),
    updateSettings: (data: any) => apiClient.patch('/store/settings', data).then(r => r.data),
  },
  onboarding: {
    registerStore: (data: any) => apiClient.post('/onboarding/store', data).then(r => r.data),
  },
  purchase: {
    getVendors: (q?: string) => apiClient.get('/purchase/vendors', { params: { q } }).then(r => r.data),
    getGRNHistory: () => apiClient.get('/purchase/grn/history').then(r => r.data),
    getGRNLines: (ctrlNo: number) => apiClient.get(`/purchase/grn/${ctrlNo}/lines`).then(r => r.data),
    finalizeGRN: (data: any) => apiClient.post('/purchase/grn/finalize', data).then(r => r.data),
  },
  users: {
    list: () => apiClient.get('/users/').then(r => r.data),
    create: (data: any) => apiClient.post('/users/', data).then(r => r.data),
    update: (id: string, data: any) => apiClient.put(`/users/${id}`, data).then(r => r.data),
    deactivate: (id: string) => apiClient.put(`/users/${id}`, { active: false }).then(r => r.data),
    getMe: () => apiClient.get('/users/me').then(r => r.data),
    updatePreferences: (data: any) => apiClient.patch('/users/me/preferences', data).then(r => r.data),
  },
  config: {
    getUIFields: (screen: string) => apiClient.get(`/config/ui-fields/${screen}`).then(r => r.data),
    getLegacyMask: (trnType: number) => apiClient.get('/masks/entry', { params: { trn_type: trnType } }).then(r => r.data),
    upsertUIField: (data: any) => apiClient.post('/config/ui-fields', data).then(r => r.data),
    listPrintTemplates: (type?: string) => apiClient.get('/config/print-templates', { params: { template_type: type } }).then(r => r.data),
    createPrintTemplate: (data: any) => apiClient.post('/config/print-templates', data).then(r => r.data),
    listAttributeAliases: () => apiClient.get('/config/attribute-aliases').then(r => r.data),
    upsertAttributeAlias: (data: any) => apiClient.post('/config/attribute-aliases', data).then(r => r.data),
    getCategories: () => apiClient.get('/config/sysparam/categories').then(r => r.data),
    getSysParams: (cat?: string) => apiClient.get('/config/sysparam', { params: { category: cat } }).then(r => r.data),
    updateSysParam: (code: string, val: any) => apiClient.patch(`/config/sysparam/${code}`, { value: val }).then(r => r.data),
  },
  masks: {
    getEntry: (trnType: number) => apiClient.get('/masks/entry', { params: { trn_type: trnType } }).then(r => r.data),
    getValidation: (trnType: number) => apiClient.get('/masks/validation', { params: { trn_type: trnType } }).then(r => r.data),
    getHotkeys: (trnType: number) => apiClient.get('/masks/hotkeys', { params: { trn_type: trnType } }).then(r => r.data),
    getZoneC: (trnType: number) => apiClient.get('/masks/zone_c', { params: { trn_type: trnType } }).then(r => r.data),
    getPermissions: (trnType: number) => apiClient.get('/masks/permissions', { params: { trn_type: trnType } }).then(r => r.data),
  },
  departments: {
    list: (level?: number, parentId?: string) => apiClient.get('/departments/', { params: { level, parent_id: parentId } }).then(r => r.data),
    create: (data: any) => apiClient.post('/departments/', data).then(r => r.data),
  },
  stockLedger: {
    list: (limit?: number) => apiClient.get('/stock-ledger/transactions', { params: { limit } }).then(r => r.data),
    getDetails: (ctrlNo: number) => apiClient.get(`/stock-ledger/transactions/${ctrlNo}`).then(r => r.data),
  },
  intelligence: {
    getDoc: () => apiClient.get('/intelligence/doc').then(r => r.data),
    getRiskSummary: () => apiClient.get('/intelligence/risk-summary').then(r => r.data),
  },
  warehouse: {
    getDashboard: () => apiClient.get('/warehouse/dashboard').then(r => r.data),
    getStores: () => apiClient.get('/warehouse/stores').then(r => r.data),
    transfer: (data: unknown) => apiClient.post('/warehouse/transfer', data).then(r => r.data),
    adjust: (data: unknown) => apiClient.post('/warehouse/adjustment', data).then(r => r.data),
    assignBin: (data: unknown) => apiClient.post('/warehouse/bin-assignment', data).then(r => r.data),
  },
  legacy: {
    listTables: () => apiClient.get('/legacy/tables').then(r => r.data),
    getData: (table: string, params?: any) => apiClient.get(`/legacy/${table}`, { params }).then(r => r.data),
    getSchema: (table: string) => apiClient.get(`/legacy/${table}/schema`).then(r => r.data),
    patchData: (table: string, id: string, data: any) => apiClient.patch(`/legacy/${table}/${id}`, data).then(r => r.data),
    bulkUpdate: (table: string, items: any[]) => apiClient.post(`/legacy/${table}/bulk`, items).then(r => r.data),
    // Alias: bulkUpsert mirrors bulkUpdate for cascade sheet saves
    // [R1-C] GOVERNANCE FIX: 403 errors now propagate correctly.
    // A 403 on a bulk save means authentication has expired — this must
    // surface to the operator, not silently succeed with mocked data.
    // Data loss prevention is a higher priority than UI continuity.
    bulkUpsert: (table: string, items: any[]) =>
      apiClient.post(`/legacy/${table}/bulk`, items).then(r => r.data),
    saveMaster: (table: string, data: any) => apiClient.post(`/legacy/master/${table}`, data).then(r => r.data),
    deleteRecord: (table: string, filters: string) => apiClient.delete(`/legacy/${table}`, { params: { filters } }).then(r => r.data),
    runWave1Migration: () => apiClient.post('/legacy/migrate/wave1').then(r => r.data),
  },
  items: {
    // S9 cascade pipeline: GenLookup → CLASS12COMBO → SUBCLASS1CAT → SUBCLASS2CAT → SIZECAT → ItemMaster → StockMaster
    batchCreate: (payload: any) => apiClient.post('/items/batch', payload).then(r => r.data),
    syncLookups: (payload: any) => apiClient.post('/items/lookup/sync', payload).then(r => r.data),
    getCaptions: () => apiClient.get('/items/captions').then(r => r.data),
    list: (params?: any) => apiClient.get('/items/', { params }).then(r => r.data),
    get: (stockno: string) => apiClient.get(`/items/${stockno}`).then(r => r.data),
    updatePrice: (stockno: string, data: any) => apiClient.patch(`/items/${stockno}/price`, data).then(r => r.data),
    getStockMatrix: (stockno: string) => apiClient.get(`/items/${stockno}/stock-matrix`).then(r => r.data),
    // ── Matrix Generator Data Feed ─────────────────────────────────────────
    // GET /items/class12combo?class1cd=... → combo rows for Matrix panel
    getComboValues: (class1cd: string, pivot: string) =>
      apiClient.get('/items/class12combo', { params: { class1cd, limit: 500 } }).then(r => r.data),
    // GET /items/sizecat?class1cd=...&class2cd=... → size rows for selected brand
    getSizeCat: (class1cd: string, class2cd: string) =>
      apiClient.get('/items/sizecat', { params: { class1cd, class2cd, limit: 200 } }).then(r => r.data),
    // GET /items/class1list → distinct Class1 codes for Matrix Class1 selector
    getClass1List: (class2cd?: string) =>
      apiClient.get('/items/class1list', { params: { class2cd } }).then(r => r.data),
    // GET /items/class2list → distinct Class2 codes for a given Class1 (dependent dropdown)
    getClass2List: (class1cd?: string) =>
      apiClient.get('/items/class2list', { params: { class1cd } }).then(r => r.data),
  },
  tally: {
    getStatus: () => apiClient.get('/tally/status').then(r => r.data),
    pushBill: (billNo: string) => apiClient.post(`/tally/push/${billNo}`).then(r => r.data),
    runSync: () => apiClient.post('/tally/sync/run').then(r => r.data),
    gatewayHealth: (url?: string) => apiClient.get('/tally/gateway/health', { params: { gateway_url: url } }).then(r => r.data),
  },
  loyalty: {
    getProfile: (partnerId: string) => apiClient.get(`/loyalty/profile/${partnerId}`).then(r => r.data),
    search: (q: string) => apiClient.get('/loyalty/search', { params: { q } }).then(r => r.data),
    redeem: (data: { partner_id: string; points: number; bill_total_paise: number }) => apiClient.post('/loyalty/redeem', data).then(r => r.data),
    adjust: (data: { partner_id: string; points: number; reason: string }) => apiClient.post('/loyalty/adjust', data).then(r => r.data),
    batchUpgrade: () => apiClient.post('/loyalty/batch-upgrade').then(r => r.data),
    getTiers: () => apiClient.get('/loyalty/tiers').then(r => r.data),
  },
  whatsapp: {
    getConfig: () => apiClient.get('/whatsapp/config').then(r => r.data),
    updateConfig: (data: any) => apiClient.post('/whatsapp/config', data).then(r => r.data),
    getStatus: () => apiClient.get('/whatsapp/status').then(r => r.data),
    sendReceipt: (data: any) => apiClient.post('/whatsapp/send/receipt', data).then(r => r.data),
    sendDayEnd: (data: any) => apiClient.post('/whatsapp/send/day-end', data).then(r => r.data),
    sendLowStock: (data: any) => apiClient.post('/whatsapp/send/low-stock', data).then(r => r.data),
  },
  ecommerce: {
    getProducts: () => apiClient.get('/ecommerce/products').then(r => r.data),
    checkout: (data: any) => apiClient.post('/ecommerce/checkout', data).then(r => r.data),
    getOrders: () => apiClient.get('/ecommerce/orders').then(r => r.data),
    syncInventory: () => apiClient.post('/ecommerce/sync/inventory').then(r => r.data),
  },
  offline: {
    getStatus: () => apiClient.get('/offline/status').then(r => r.data),
  },
  connectivity: {
    healthCheck: async (url: string, signal?: AbortSignal) => {
      try {
        const response = await fetch(`${url}/api/v1/health`, { 
          signal,
          headers: { 'Accept': 'application/json' }
        });
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        return data;
      } catch (err: any) {
        if (err.name === 'AbortError') throw err;
        throw new Error(err.message || 'Node unreachable');
      }
    },
  }
}
